import { cmsStrings } from "../terminology";

export type NavItem = {
  to: string;
  label: string;
  /** Exact pathname match (default true for all D3 items). */
  end?: boolean;
  permission?: string;
  /** Visual nested deep-link under a parent item (reserved; rail uses primaries only). */
  nested?: boolean;
};

export type NavSection = {
  id: string;
  title: string;
  items: NavItem[];
};

const { groups, nav } = cmsStrings;

/**
 * Six-group operational IA (V1 shell).
 * Labels from L3A terminology catalog.
 * Rail shows primary destinations only (owner GO 2026-09-16).
 * Deep-links (drift / alerts / activity / backup) stay on pages + system search.
 * C9A adds `/organizations` (org.create) in Administration.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: "overview",
    title: groups.overview,
    items: [{ to: "/", label: nav.overview, end: true }],
  },
  {
    id: "content",
    title: groups.content,
    items: [
      { to: "/announcements", label: nav.announcements },
      { to: "/promotions", label: nav.promotions },
      { to: "/sponsored", label: nav.sponsored },
    ],
  },
  {
    id: "network",
    title: groups.network,
    items: [
      { to: "/sites", label: nav.sitesAndGroups },
      {
        to: "/reseller-profiles",
        label: nav.resellerProfiles,
        permission: "reseller.profile.read",
      },
    ],
  },
  {
    id: "release",
    title: groups.release,
    items: [
      {
        to: "/portal-bundles",
        label: nav.bundle,
        permission: "portal.bundle.generate",
      },
      {
        to: "/fleet",
        label: nav.fleet,
        permission: "fleet.inventory.read",
      },
      { to: "/versions", label: nav.versions },
    ],
  },
  {
    id: "operations",
    title: groups.operations,
    items: [
      {
        to: "/observability",
        label: nav.observability,
        permission: "audit.read",
      },
    ],
  },
  {
    id: "administration",
    title: groups.administration,
    items: [
      {
        to: "/memberships",
        label: nav.memberships,
        permission: "org.membership.manage",
      },
      {
        to: "/organizations",
        label: nav.organizations,
        permission: "org.create",
      },
      {
        to: "/entitlements",
        label: nav.entitlements,
        permission: "org.entitlements.read",
      },
      {
        to: "/users",
        label: nav.users,
        permission: "users.manage",
      },
      {
        to: "/settings",
        label: nav.settings,
        permission: "settings.manage",
      },
      { to: "/audit", label: nav.audit },
    ],
  },
];

/** Whether a nav destination is active for the current location. */
export function isNavItemActive(
  item: NavItem,
  pathname: string,
  search: string,
): boolean {
  const target = new URL(item.to, "http://nav.local");
  if (pathname !== target.pathname) return false;

  const want = target.searchParams;
  const have = new URLSearchParams(search);

  if ([...want.keys()].length > 0) {
    for (const [key, value] of want.entries()) {
      if (have.get(key) !== value) return false;
    }
    return true;
  }

  // Primary rail items stay active for any tab/section query on the same path.
  return true;
}
